import React, { createContext, FC, useContext, useMemo, useReducer } from "react";

export interface State {
  displayModal: boolean;
  modalView: string | null;
}
interface INewlyAddedPayment {
  cardBrand: string;
  lastFourDigits: string;
  cardId: string;
}

interface IInitialState {
  displayModal: boolean;
  modalView: string | null;
  source: string;
  params: Record<string, unknown>;
  appointmentPlaceId: string;
  wellnessSelectedPetId: string;
  retirePetId: string;
  newlyAddedPayment: INewlyAddedPayment;
  appointmentErrorCode: string;
}

const initialState: IInitialState = {
  displayModal: false,
  modalView: null,
  source: "",
  wellnessSelectedPetId: "",
  appointmentPlaceId: "",
  retirePetId: "",
  newlyAddedPayment: {
    cardBrand: "",
    lastFourDigits: "",
    cardId: "",
  },
  appointmentErrorCode: "",
  params: {},
};

interface IModalContext extends IInitialState {
  openModal: (view: string, source?: string, params?: Record<string, unknown>) => void;
  closeModal: () => void;
  setAppointmentId: (appointmentId: string) => void;
  setRetirePetId: (retirePetId: string) => void;
  setWellnessSelectedPetId: (wellnessPetid: string) => void;
  setNewlyAddedPayment: (newlyAddedPayment: INewlyAddedPayment) => void;
  setAppointmentErrorCode: (appointmentErrorCode: string) => void;
}

type Action =
  | {
    type: "OPEN_MODAL"
    view: string
    source?: string
    params?: Record<string, unknown>
  }
  | {
    type: "CLOSE_MODAL"
  }
  | {
    type: "REVIEW_APPOINTMENT_ID"
    appointmentId: string
    }
  | {
    type: "RETIRE_PET_ID"
    retirePetId: string
  }
  | {
    type: "WELLNESS_SELECTED_PET_ID"
    wellnessSelectedPetId: string
  }
  | {
    type: "NEWLY_ADDED_PAYMENT"
    newlyAddedPayment: INewlyAddedPayment
  }
  | {
    type: "APPOINTMENT_ERROR_CODE"
    appointmentErrorCode: string
  }

export const ModalContext = createContext<State | any>(initialState);
ModalContext.displayName = "ModalContext";

function modalReducer(state: State, action: Action) {
  switch (action.type) {
  case "OPEN_MODAL": {
    return {
      ...state,
      modalView: action.view,
      source: action.source,
      params: action.params,
      displayModal: true,
    };
  }
  case "CLOSE_MODAL": {
    return {
      ...state,
      modalView: null,
      source: "",
      displayModal: false,
    };
  }
  case "REVIEW_APPOINTMENT_ID": {
    return {
      ...state,
      appointmentPlaceId: action.appointmentId,
    };
  }
  case "RETIRE_PET_ID": {
    return {
      ...state,
      retirePetId: action.retirePetId,
    };
  }
  case "WELLNESS_SELECTED_PET_ID": {
    return {
      ...state,
      wellnessSelectedPetId: action.wellnessSelectedPetId,
    };
  }
  case "NEWLY_ADDED_PAYMENT": {
    return {
      ...state,
      newlyAddedPayment: action.newlyAddedPayment,
    };
  }
  case "APPOINTMENT_ERROR_CODE": {
    return {
      ...state,
      appointmentErrorCode: action.appointmentErrorCode,
    };
  }
  }
}

export const ModalProvider: FC = (props) => {
  const [state, dispatch] = useReducer(modalReducer, initialState);
  const openModal = (view: string, source?: string, params?: Record<string, unknown>) =>
    dispatch({ type: "OPEN_MODAL", view, source, params });
  const closeModal = () => dispatch({ type: "CLOSE_MODAL" });
  const setAppointmentId = (appointmentId: string) => dispatch({ type: "REVIEW_APPOINTMENT_ID", appointmentId });
  const setRetirePetId = (retirePetId: string) => dispatch({ type: "RETIRE_PET_ID", retirePetId });
  // eslint-disable-next-line max-len
  const setWellnessSelectedPetId = (wellnessSelectedPetId: string) => dispatch({ type: "WELLNESS_SELECTED_PET_ID", wellnessSelectedPetId });
  const setAppointmentErrorCode = (appointmentErrorCode: string) =>
    dispatch({ type: "APPOINTMENT_ERROR_CODE", appointmentErrorCode });
  const setNewlyAddedPayment = (newlyAddedPayment: INewlyAddedPayment) =>
    dispatch({ type: "NEWLY_ADDED_PAYMENT", newlyAddedPayment });

  const value = useMemo(
    () => ({
      ...state,
      openModal,
      closeModal,
      setAppointmentId,
      setRetirePetId,
      setWellnessSelectedPetId,
      setNewlyAddedPayment,
      setAppointmentErrorCode,
    }),
    [state],
  );

  return <ModalContext.Provider value={value} {...props} />;
};

export const useModal = (): IModalContext => {
  const context = useContext(ModalContext);

  if (context === undefined) {
    throw new Error("useUI must be used within a UIProvider");
  }

  return context;
};
